import { NgModule } from '@angular/core';
import { RouterModule, Routes, TitleStrategy } from '@angular/router';
import { AuthGuard } from '@bn/auth/guards';
import { UserStateResolver, singletonsResolver } from '@bn/auth/resolvers';
import { BnTemplatePageTitleStrategy } from '@bn/dashboard-shared/resolvers';
import { FeatureFlags } from '@bn/shared/entities/enums';
import { AccountSuspendedGuard, EmailNotVerifiedGuard, FeatureFlagGuard, UserPermissionsGuard } from '@bn/shared/guards';
import { AIGatewayRoutes, AppRoutes, StreamAppRoutes } from './core/src/lib/enums';
import { DashboardLayoutComponent } from './layout/components';
import { DemoComponentsPage } from './pages/components/demo-components.page';
import { NotFoundPage } from './pages/errors';
import { LogoutPage } from './pages/logout/logout.page';
import { ORIGIN_PATHS } from './pages/origin-error/static-data';

export const appRoutes: Routes = [
  {
    path: AppRoutes.Auth,
    loadChildren: () => import('@bn/auth').then((module) => module.AuthModule),
    title: 'bunny.net - The Content Delivery platform that truly Hops!',
  },
  {
    path: `${StreamAppRoutes.livestreamPreview}/:videoLibraryId/:livestreamId`,
    loadComponent: () =>
      import('./pages/stream/pages/edit-video-library/pages/edit-video-library-livestream-preview/edit-video-library-livestream-preview.page').then(
        (m) => m.EditVideoLibraryLivestreamPreviewPage,
      ),
    canActivate: [AuthGuard, FeatureFlagGuard],
    data: {
      featureFlag: FeatureFlags.livestream,
    },
    title: 'Livestream Preview',
  },
  {
    path: 'deploy',
    loadComponent: () => import('./pages/magic-containers/pages/deploy').then((m) => m.MCDeployPage),
    title: 'Deploy',
  },
  {
    path: `${AppRoutes.Partner}/accept-invite`,
    loadComponent: () => import('./pages/partner/pages/accept-invite/accept-invite.page').then((m) => m.AcceptInvitePage),
    canActivate: [AuthGuard],
    title: 'Accept Partner Invitation',
  },
  {
    path: `${AppRoutes.Zendesk}/login`,
    loadComponent: () => import('./pages/zendesk/pages/login/zendesk-login-page/zendesk-login.page').then((m) => m.ZendeskLoginPage),
    canActivate: [AuthGuard],
    title: 'Zendesk - Login',
  },
  {
    path: '',
    canActivate: [AuthGuard],
    component: DashboardLayoutComponent,
    resolve: {
      singletons: singletonsResolver,
      userDetails: UserStateResolver,
    },
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/overview/overview.module').then((module) => module.OverviewModule),
        canActivate: [AccountSuspendedGuard],
        // pathMatch: 'full',
        title: 'Account Overview',
      },
      {
        path: AppRoutes.Welcome,
        loadChildren: () => import('./pages/overview/overview.module').then((module) => module.OverviewModule),
        canActivate: [AccountSuspendedGuard],
        pathMatch: 'full',
        title: 'Welcome',
      },
      {
        path: AppRoutes.CDN,
        loadChildren: () => import('./pages/cdn/cdn.module').then((module) => module.CdnModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Pull Zones',
      },
      {
        path: AppRoutes.Storage,
        loadChildren: () => import('./pages/storage/storage.module').then((module) => module.StorageModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Storage Zones',
      },
      {
        path: AppRoutes.DNS,
        loadChildren: () => import('./pages/dns/dns.module').then((module) => module.DnsModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'DNS Zones',
      },
      {
        path: AppRoutes.Database,
        loadChildren: () => import('@bn/app-database/database.module').then((module) => module.DatabaseModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Database',
      },
      {
        path: AppRoutes.Script,
        loadChildren: () => import('./pages/scripts/scripts.module').then((module) => module.ScriptModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Scripts',
      },
      {
        path: AppRoutes.MagicContainers,
        loadChildren: () => import('./pages/magic-containers/magic-containers.module').then((module) => module.MagicContainersModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Magic Containers',
      },
      {
        path: AppRoutes.AIGateway,
        redirectTo: `${AppRoutes.AIGateway}/${AIGatewayRoutes.Overview}`,
        pathMatch: 'full',
      },
      {
        path: AppRoutes.AIGateway,
        loadChildren: () => import('./pages/ai-gateway/ai-gateway.module').then((module) => module.AIGatewayModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard, FeatureFlagGuard],
        data: {
          roleIds: ['SubuserManage'],
          featureFlag: FeatureFlags.aiGateway,
        },
        title: 'AI Gateway',
      },
      {
        path: 'deploy',
        loadComponent: () => import('./pages/magic-containers/pages/deploy').then((m) => m.MCDeployPage),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Deploy',
      },
      /*      {
        path: `${AppRoutes.MagicContainers}/waitlist`,
        component: MagicContainersPublicBetaPage,
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard, NegatedFeatureFlagGuard],
        data: {
          roleIds: ['SubuserManage'],
          featureFlag: FeatureFlags.mc,
        },
        title: 'Magic containers',
      },*/
      {
        path: AppRoutes.Stream,
        loadChildren: () => import('./pages/stream/stream.module').then((module) => module.StreamModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Stream',
      },
      {
        path: AppRoutes.Statistics,
        loadChildren: () => import('./pages/statistics/statistics.module').then((module) => module.StatisticsModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Statistics',
      },
      {
        path: ORIGIN_PATHS.base,
        loadChildren: () => import('./pages/origin-error/pages/page.routes').then((module) => module.EXCHANGE_ROUTES),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: { roleIds: ['SubuserManage'] },
        title: BnTemplatePageTitleStrategy.TITLE_IGNORE,
      },
      {
        path: AppRoutes.Purge,
        loadChildren: () => import('./pages/purge/purge.module').then((module) => module.PurgeModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Purge',
      },
      {
        path: AppRoutes.Logs,
        loadChildren: () => import('./pages/logs/logs-routing').then((module) => module.routes),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Log explorer',
      },
      {
        path: AppRoutes.Surveys,
        loadChildren: () => import('@bn/app-surveys/surveys.module').then((module) => module.SurveysModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard, FeatureFlagGuard],
        data: {
          roleIds: ['Agent'],
          featureFlag: FeatureFlags.surveys,
        },
        title: 'Surveys',
      },
      {
        path: AppRoutes.Notifications,
        loadChildren: () => import('./pages/notifications/notifications.module').then((module) => module.NotificationsModule),
        canActivate: [AccountSuspendedGuard],
        title: 'Changelog & Notifications',
      },
      {
        path: AppRoutes.Account,
        loadChildren: () => import('./pages/account/account.module').then((module) => module.AccountModule),
        title: 'Account settings',
      },
      {
        path: AppRoutes.Support,
        loadChildren: () => import('./pages/support/support.module').then((module) => module.SupportModule),
        canActivate: [UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserSupport'],
        },
        title: 'Support',
      },
      {
        path: AppRoutes.Errors,
        loadChildren: () => import('./pages/errors/errors.module').then((module) => module.ErrorsModule),
        title: 'Errors',
      },
      {
        path: 'logout',
        component: LogoutPage,
        title: 'Logout',
      },
      {
        path: 'components',
        component: DemoComponentsPage,
        title: 'Demo Components',
      },
      {
        path: AppRoutes.NotFound,
        component: NotFoundPage,
        title: 'Page Not Found',
      },
    ],
  },
  { path: '404', component: NotFoundPage, title: 'Page Not Found' },
  { path: AppRoutes.NotFound, redirectTo: '' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(appRoutes, {
      paramsInheritanceStrategy: 'always',
      anchorScrolling: 'enabled',
      scrollOffset: [0, 80],
      // enableTracing: true, // <-- debugging purposes only
      bindToComponentInputs: true,
    }),
  ],
  exports: [RouterModule],
  providers: [{ provide: TitleStrategy, useClass: BnTemplatePageTitleStrategy }],
})
export class AppRoutingModule {}
